import { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';
import { token } from '@atlaskit/tokens';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import {
  Breadcrumbs,
  BreadcrumbsItem,
  FluidContainer,
  NoResultView,
} from '@atlassian/mpac-primitives';
import { useHistory, useParams } from '@atlassian/mpac-ui';
import { css } from '@compiled/react';
import { useState } from 'react';

import { getBundleConfig } from '../config/bundleConfig';
import { useBundleAnalytics } from '../hooks/useBundleAnalytics';
import { AppsSection } from './AppsSection';
import { BenefitsSection } from './BenefitsSection';
import { BundleSection } from './BundleSection';
import { FooterSection } from './FooterSection';
import { HeroSection } from './HeroSection';
import { RequestBundleDialog } from './RequestBundleDialog';

const bundlePageStyles = css({});

const breadcrumbsStyle = css({
  marginTop: token('space.200'),
  marginBottom: token('space.200'),
});

interface BundlePageFlagsProps {
  showFlag: 'success' | 'failure' | null;
  onDismiss: () => void;
}

const BundlePageFlags: React.FC<BundlePageFlagsProps> = ({ showFlag, onDismiss }) => {
  if (!showFlag) return null;

  return (
    <FlagGroup>
      {showFlag === 'success' ? (
        <AutoDismissFlag
          id="bundle-feedback-submitted"
          icon={<CheckCircleIcon label="Success" primaryColor={token('color.icon.success')} />}
          title="Response submitted"
          description="Thanks for sharing your app bundles feedback!"
          onDismissed={onDismiss}
          actions={[{ content: 'Got it', onClick: onDismiss }]}
        />
      ) : (
        <AutoDismissFlag
          id="bundle-feedback-not-submitted"
          icon={<CrossCircleIcon label="Failure" primaryColor={token('color.icon.danger')} />}
          title="Error submitting response"
          description="We couldn’t save your feedback. Please try again."
          onDismissed={onDismiss}
          actions={[{ content: 'Got it', onClick: onDismiss }]}
        />
      )}
    </FlagGroup>
  );
};

// eslint-disable-next-line max-lines-per-function
const BundlePage = () => {
  const history = useHistory();
  const { enableStaticBundlePages, enableDevopsEssentialsBundlePage, enableIssueTerminology } =
    useStatsig();

  const { bundleId } = useParams();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showFlag, setShowFlag] = useState<'success' | 'failure' | null>(null);
  const { trackRequestBundleClick, trackSubmitResponseClick } = useBundleAnalytics();

  if (!bundleId) {
    return (
      <NoResultView
        title="Bundle not found"
        description="The bundle you're looking for doesn't exist."
        showClearFilterButton={false}
      />
    );
  }

  // Show all bundle pages if enableStaticBundlePages is true
  // Otherwise, only show devops bundle if enableDevopsEssentialsBundlePage is true and bundleId is 'devops'
  const isStaticBundlePagesEnabled = enableStaticBundlePages({ fireExperimentExposure: true });
  const isDevopsBundleAllowed =
    enableDevopsEssentialsBundlePage() && bundleId === 'devopsEssentials';

  if (!isStaticBundlePagesEnabled && !isDevopsBundleAllowed) {
    return (
      <NoResultView
        title="Page not found"
        description="Page you're looking for doesn't exist."
        showClearFilterButton={false}
      />
    );
  }

  try {
    const bundleConfig = getBundleConfig(bundleId, enableIssueTerminology());

    const handleOpenDialog = (section: 'hero' | 'footer') => {
      trackRequestBundleClick({ bundleId, section });
      setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
      setIsDialogOpen(false);
    };

    const handleSubmitRequest = async (formData: any): Promise<void> => {
      try {
        trackSubmitResponseClick({
          bundleId,
          pricingPreference: formData.pricingPreference,
          discoveryPreferences: formData.discoveryPreferences,
          status: 'success',
        });
        setShowFlag('success');
      } catch (error) {
        trackSubmitResponseClick({
          bundleId,
          pricingPreference: formData.pricingPreference,
          discoveryPreferences: formData.discoveryPreferences,
          status: 'failure',
        });
        setShowFlag('failure');
      }
    };

    const handleBreadcrumbsClick = (event: React.MouseEvent<Element, MouseEvent>) => {
      event.preventDefault();
      history?.push('/');
    };

    return (
      <div css={bundlePageStyles}>
        <BundlePageFlags showFlag={showFlag} onDismiss={() => setShowFlag(null)} />

        <FluidContainer>
          <div css={breadcrumbsStyle}>
            <Breadcrumbs>
              <BreadcrumbsItem href="/" text="Marketplace" onClick={handleBreadcrumbsClick} />
              <BreadcrumbsItem
                text={bundleConfig.hero.breadcrumbTitle}
                onClick={(e) => e.preventDefault()}
              />
            </Breadcrumbs>
          </div>
          <HeroSection
            bundleConfig={bundleConfig}
            onRequestClick={() => handleOpenDialog('hero')}
          />
        </FluidContainer>

        <BenefitsSection bundleConfig={bundleConfig} />

        <FluidContainer>
          <AppsSection bundleConfig={bundleConfig} />
        </FluidContainer>

        <FluidContainer>
          <FooterSection
            bundleConfig={bundleConfig}
            onRequestClick={() => handleOpenDialog('footer')}
          />
        </FluidContainer>

        {/* do not show BundleSection for devopsEssentials bundle */}
        {bundleId !== 'devopsEssentials' && <BundleSection bundleId={bundleId} />}

        <RequestBundleDialog
          isOpen={isDialogOpen}
          onClose={handleCloseDialog}
          onSubmit={handleSubmitRequest}
        />
      </div>
    );
  } catch (error) {
    return (
      <NoResultView
        title="Bundle not found"
        description="The bundle you're looking for doesn't exist or has been removed."
        showClearFilterButton={false}
      />
    );
  }
};

export default BundlePage;
